/**
 * @file useIsMobile.ts
 * @description Hook that determines whether Squad Planning should render its
 * mobile layout. Delegates to the project-wide `useMobileScreen` split so the
 * feature cannot disagree with the screen that hosts it.
 * @module features/squad-planning/hooks
 */

import useMobileScreen from "@/hooks/useMobileScreen";

/**
 * Returns whether the current window is a small screen.
 *
 * SE-8368: this used to cut at `width < 768` (NativeWind's `md:`) while
 * `SquadScreenContainer` — which decides whether the desktop scenario-pills
 * row mounts, and with it the ONLY route to the Actions Summary panel — cuts
 * at `useMobileScreen()`'s `width <= 1280`. Between 768 and 1280 the planning
 * content rendered its desktop layout (no mobile Overview tab) while the host
 * had already unmounted the desktop actions bar, leaving no UI path at all to
 * the Actions Summary panel. Two thresholds on one screen is the defect, so
 * this hook no longer owns one: it forwards to the project-wide split and can
 * never drift from it again. `useIsMobile.test.ts` pins the two together.
 *
 * Nothing in `src/features/squad-planning` pairs this hook with a NativeWind
 * `md:` utility, so the old 768 alignment was not load-bearing.
 *
 * Kept as a named hook rather than replaced at its five call sites: it is the
 * seam squad-planning container tests mock, and the indirection is what let
 * this move be one edit instead of five.
 * @returns true when the viewport is at or below the project-wide small-screen
 * threshold
 */
export function useIsMobile(): boolean {
  return useMobileScreen();
}
